/**
 * نامِ کاربری — ساختن، پاک‌سازی، و پیدا کردنِ یکی که آزاد باشد.
 *
 * ---------- چرا این فایل درست شد ----------
 * تا امروز این فیلد سرِ ثبت‌نام **اختیاری** بود و اگر خالی می‌ماند خودمان
 * یکی می‌ساختیم، درست وسطِ `createMine`: اسمِ نمایشی را می‌گرفتیم، هرچه
 * غیرِ `a-z0-9_` بود دور می‌ریختیم، و اگر چیزی باقی نمی‌ماند `user` +
 * پنج رقم از ساعتِ سیستم. سه چیز از همین یک خط بیرون می‌آمد:
 *
 *   • اسمِ فارسی/عربی/چینی بعد از فیلتر **هیچ** می‌شد. یعنی برای مخاطبِ
 *     اصلیِ این اپ، حالتِ پیش‌فرض همان `user48213` بود — و چون نامِ کاربری
 *     بعداً عوض نمی‌شود (`updateMine` اصلاً این فیلد را ندارد)، تا ابد.
 *   • دو نفر با اسمِ یکسان یعنی خطای یکتاییِ دیتابیس روی فیلدی که کاربر
 *     اصلاً پرش نکرده بود؛ پیامش می‌شد «این نام کاربری گرفته شده».
 *   • و `user` + پنج رقم فقط ۱۰۰٬۰۰۰ حالت دارد، پس خودش هم تصادم می‌کرد.
 *
 * حالا فیلد اجباری است، ولی خالی به کاربر تحویل داده نمی‌شود: پیشنهاد از
 * روی اسم (و اگر اسم لاتین نبود، از روی ایمیل) ساخته و **نشان داده** می‌شود
 * تا کاربر ببیند و تاییدش کند. تصادم هم پیش از ساختِ حساب معلوم می‌شود، نه
 * به شکلِ خطای دیتابیس بعدش.
 *
 * ---------- چرا این‌جا و نه توی فرم‌ها ----------
 * دو فرمِ ثبت‌نام داریم (`apps/mobile/app/auth.tsx` و
 * `apps/web/src/features/auth/CreateProfilePage.tsx`) و هر قاعده‌ای که در
 * هر دو کپی شود، روزی در یکی عوض می‌شود و در آن یکی نه. این‌جا تابع‌های
 * خالص‌اند، پس با `npm test` سنجیده می‌شوند بی‌آنکه مرورگر یا گوشی لازم باشد.
 */

/** همان ۲۰ حرفی که ستونِ `profiles.username` از روزِ اول با آن ساخته شد. */
export const USERNAME_MAX = 20;

/**
 * کوتاه‌تر از این نه.
 *
 * دلیلش زیبایی نیست: نامِ کاربری تنها راهِ **بی‌ابهامِ** پیدا کردنِ یک آدم
 * است («افزودن دوست» با همین شروع می‌شود)، و یک یا دو حرف هم زود تمام
 * می‌شود و هم به‌سختی گفته می‌شود.
 */
export const USERNAME_MIN = 3;

/**
 * هرچه کاربر تایپ کرده → چیزی که می‌شود در ستون نوشت.
 *
 * همان قاعده‌ی قبلی، فقط از دلِ `createMine` به این‌جا آمده تا فرم هم
 * بتواند همان لحظه‌ی تایپ اعمالش کند: اگر حرفی قرار است دور ریخته شود،
 * کاربر باید همان‌جا ببیندش، نه بعد از ساختِ حساب.
 *
 * شروع فقط با حرفِ انگلیسی: `92ali` و `_ali` تا رسیدن به اولین حرف
 * می‌ریزند و `ali` می‌مانند. نامی که با عدد شروع شود همه‌جا — از منشن تا
 * آدرسِ پروفایل — با شماره‌ی تلفن و شناسه‌های عددی قاطی می‌شود.
 */
export function normalizeUsername(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9_]/g, '')
    .replace(/^[^a-z]+/, '').slice(0, USERNAME_MAX);
}

/**
 * پیشنهادِ اولیه برای کسی که هنوز چیزی ننوشته.
 *
 * ترتیبش عمدی است: اول اسمِ نمایشی، چون تنها چیزی است که کاربر خودش
 * انتخابش کرده. اگر بعد از پاک‌سازی چیزی نماند — که برای اسمِ فارسی همیشه
 * همین‌طور است — بخشِ قبلِ @ ایمیل، که تقریباً همیشه چیزی شبیهِ اسمِ آدم
 * است و خودش انتخابش کرده. و اگر آن هم نشد، رشته‌ی خالی برمی‌گردانیم و
 * کاربر خودش می‌نویسد؛ عمداً `user48213` تولید نمی‌کنیم — همان چیزی که
 * این فایل برای جمع کردنش نوشته شد.
 */
export function suggestUsername(displayName: string, email?: string | null): string {
  const fromName = normalizeUsername(displayName);
  if (fromName.length >= USERNAME_MIN) return fromName;
  const fromEmail = normalizeUsername((email ?? '').split('@')[0] ?? '');
  if (fromEmail.length >= USERNAME_MIN) return fromEmail;
  return '';
}

/**
 * «پرویز، پرویز۲، پرویز۳ …» — نامزدهای پشتِ سرِ هم برای یک پایه.
 *
 * عدد **آخر** می‌چسبد و اگر جا کم بیاید از پایه کوتاه می‌شود، نه از عدد:
 * `alexanderthegreatest` + `2` باید بشود `alexanderthegreates2`، نه چیزی
 * که عددش قیچی شده و دوباره با نامزدِ قبلی یکی است.
 */
export function usernameCandidate(base: string, attempt: number): string {
  if (attempt <= 1) return normalizeUsername(base);
  const suffix = String(attempt);
  return normalizeUsername(base).slice(0, USERNAME_MAX - suffix.length) + suffix;
}

/**
 * اولین نامزدی که آزاد است، یا `null` اگر تا `tries` تا هم گرفته بود.
 *
 * `isFree` را صداکننده می‌دهد تا این تابع به دیتابیس وابسته نباشد و با
 * `npm test` سنجیده شود. فقط برای پیشنهادِ **خودکار** است: چیزی که کاربر
 * خودش تایپ کرده هرگز بی‌اجازه عوض نمی‌شود — به او می‌گوییم گرفته است و
 * انتخاب با خودش.
 */
export async function firstFreeUsername(
  base: string,
  isFree: (u: string) => Promise<boolean>,
  tries = 5,
): Promise<string | null> {
  if (normalizeUsername(base).length < USERNAME_MIN) return null;
  for (let attempt = 1; attempt <= tries; attempt += 1) {
    const candidate = usernameCandidate(base, attempt);
    // پشتِ سرِ هم و نه موازی: در حالتِ عادی همان اولی آزاد است و یک
    // رفت‌وبرگشت بس است؛ پنج‌تا فرستادن یعنی چهار کوئریِ دورریز برای
    // هر کسی که ثبت‌نام می‌کند.
    if (await isFree(candidate)) return candidate;
  }
  return null;
}
